import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BookingsAdmin } from './BookingsAdmin';
import { InvoicesPage } from './InvoicesPage';
import { ExpensesPage } from './ExpensesPage';
import { QuotationPage } from './QuotationPage';
import { LeadsPage } from './LeadsPage';
import { ReviewRequestPage } from './ReviewRequestPage';
import { PWAInstallPrompt } from './PWAInstallPrompt';
import { FileText, ClipboardList, Bell, BellOff, DollarSign, FileSignature, ShieldAlert, X, Users, Star } from 'lucide-react';
import { useBookingNotifications } from '../hooks/useBookingNotifications';
import { toast } from 'sonner';
import { getAdminDisplayName } from '../utils/adminNames';

export const AdminDashboard = ({ user }) => {
  const [activeTab, setActiveTab] = useState('bookings');
  const [loginStats, setLoginStats] = useState([]);
  const [deniedAttempts, setDeniedAttempts] = useState([]);
  const [showDenied, setShowDenied] = useState(false);
  const { permission, requestPermission, startPolling, stopPolling, isPolling } = useBookingNotifications();

  useEffect(() => {
    const opts = { withCredentials: true };
    const base = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${base}/api/auth/login-stats`, opts)
      .then((res) => setLoginStats(res.data))
      .catch(() => {});
    axios.get(`${base}/api/auth/denied-attempts`, opts)
      .then((res) => setDeniedAttempts(res.data))
      .catch(() => {});
  }, []);

  // Request notification permission and start polling on mount
  const initNotifications = useCallback(async () => {
    if (permission === 'default') {
      const granted = await requestPermission();
      if (granted) {
        toast.success('🔔 Push notifications enabled!');
        startPolling();
      } else {
        toast.info('Push notifications disabled. Enable in browser settings to receive booking alerts.');
      }
    } else if (permission === 'granted') {
      startPolling();
    }
  }, [permission, requestPermission, startPolling]);

  useEffect(() => {
    initNotifications();

    // Cleanup on unmount
    return () => {
      stopPolling();
    };
  }, [initNotifications, stopPolling]);

  const toggleNotifications = async () => {
    if (permission !== 'granted') {
      const granted = await requestPermission();
      if (granted) {
        toast.success('🔔 Push notifications enabled!');
        startPolling();
      } else {
        toast.error('Permission denied. Please enable notifications in browser settings.');
      }
    } else {
      if (isPolling) {
        stopPolling();
        toast.info('🔕 Notifications paused');
      } else {
        startPolling();
        toast.success('🔔 Notifications enabled');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* PWA Install Prompt */}
      <PWAInstallPrompt />
      
      {/* Header with Tabs */}
      <div className="bg-white shadow-md border-b border-gray-200 safe-area-top">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-2 py-3 sm:h-20 sm:py-0">
            {/* Logo and Title */}
            <div className="flex items-center min-w-0 flex-1">
              <img
                src="/sg-cabs-logo.png"
                alt="SG Cabs"
                className="h-9 w-9 sm:h-12 sm:w-12 mr-2 sm:mr-3 flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-2xl font-bold text-gray-900 leading-tight truncate">Admin Panel</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">SG Cabs Management</p>
              </div>
            </div>

            {/* User Info */}
            <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
              {/* Denied Attempts Button */}
              <button
                onClick={() => setShowDenied(true)}
                data-testid="denied-attempts-btn"
                className={`relative flex items-center px-3 py-2 rounded-lg transition-colors ${
                  deniedAttempts.length > 0
                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Denied access attempts"
                aria-label="Denied access attempts"
              >
                <ShieldAlert className="w-4 h-4" />
                {deniedAttempts.length > 0 ? (
                  <span
                    data-testid="denied-attempts-count"
                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
                  >
                    {deniedAttempts.length}
                  </span>
                ) : null}
              </button>

              {/* Notification Toggle Button */}
              <button
                onClick={toggleNotifications}
                data-testid="notification-toggle-btn"
                className={`flex items-center gap-2 px-3 py-2 sm:px-4 rounded-lg font-medium transition-colors ${
                  isPolling && permission === 'granted'
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title={isPolling ? 'Notifications enabled' : 'Enable notifications'}
                aria-label={isPolling ? 'Notifications on' : 'Notifications off'}
              >
                {isPolling && permission === 'granted' ? (
                  <>
                    <Bell className="w-4 h-4" />
                    <span className="hidden sm:inline text-sm">Notifications On</span>
                  </>
                ) : (
                  <>
                    <BellOff className="w-4 h-4" />
                    <span className="hidden sm:inline text-sm">Notifications Off</span>
                  </>
                )}
              </button>

              {/* User Info - hidden on small screens to save space */}
              <div className="text-right hidden md:block">
                <p className="text-sm text-gray-600">Logged in as</p>
                <p className="text-sm font-semibold text-gray-900" data-testid="admin-logged-in-name">{getAdminDisplayName(user)}</p>
                {loginStats.length > 0 ? (
                  <p className="text-[11px] text-gray-500" data-testid="admin-login-stats">
                    Logins — {loginStats.map((s) => `${getAdminDisplayName(s)}: ${s.count}`).join(' · ')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {/* Tabs Navigation - horizontal scroll on mobile to avoid wrapping */}
          <div
            className="flex gap-4 sm:gap-8 border-t border-gray-200 pt-3 sm:pt-4 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0"
            style={{ scrollbarWidth: 'none' }}
          >
            <button
              onClick={() => setActiveTab('bookings')}
              data-testid="tab-bookings"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'bookings'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ClipboardList className="w-5 h-5 mr-2" />
              <span className="sm:hidden">Bookings</span>
              <span className="hidden sm:inline">Booking Requests</span>
            </button>
            <button
              onClick={() => setActiveTab('invoices')}
              data-testid="tab-invoices"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'invoices'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileText className="w-5 h-5 mr-2" />
              Invoices
            </button>
            <button
              onClick={() => setActiveTab('expenses')}
              data-testid="tab-expenses"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'expenses'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <DollarSign className="w-5 h-5 mr-2" />
              Expenses
            </button>
            <button
              onClick={() => setActiveTab('quotation')}
              data-testid="tab-quotation"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'quotation'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <FileSignature className="w-5 h-5 mr-2" />
              <span className="sm:hidden">Quote</span>
              <span className="hidden sm:inline">Quotation</span>
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              data-testid="tab-leads"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'leads'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Users className="w-5 h-5 mr-2" />
              Leads
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              data-testid="tab-reviews"
              className={`flex items-center pb-3 sm:pb-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === 'reviews'
                  ? 'border-black text-black'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Star className="w-5 h-5 mr-2" />
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Denied Attempts Modal */}
      {showDenied ? (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="denied-attempts-modal">
          <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] overflow-y-auto shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 sticky top-0 bg-white">
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <ShieldAlert className="w-5 h-5 mr-2 text-red-600" />
                Denied Access Attempts
              </h2>
              <button
                onClick={() => setShowDenied(false)}
                data-testid="denied-attempts-close-btn"
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {deniedAttempts.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No denied attempts. All clear! ✅</p>
              ) : (
                <div className="space-y-3">
                  {deniedAttempts.map((a) => (
                    <div
                      key={`${a.email}-${a.attempted_at}`}
                      className="border border-red-100 bg-red-50 rounded-md px-3 py-2"
                    >
                      <p className="text-sm font-semibold text-gray-900">{a.name || 'Unknown'}</p>
                      <p className="text-xs text-gray-700">{a.email}</p>
                      <p className="text-[11px] text-gray-500 mt-1">
                        {new Date(a.attempted_at).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {/* Tab Content */}
      <div className="safe-area-bottom">
        {activeTab === 'bookings' && <BookingsAdmin hideHeader={true} />}
        {activeTab === 'invoices' && <InvoicesPage user={user} />}
        {activeTab === 'expenses' && <ExpensesPage />}
        {activeTab === 'quotation' && <QuotationPage user={user} />}
        {activeTab === 'leads' && (
          <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6">
            <LeadsPage />
          </div>
        )}
        {activeTab === 'reviews' && <ReviewRequestPage />}
      </div>
    </div>
  );
};
